/**
 * Layout Functions
 * Handle all layout concerns including columns, breaks, and section properties
 */

import {
  ComponentDefinition,
  ColumnSettings,
  isColumnsComponent,
  isSectionComponent,
  isHeadingComponent,
  isParagraphComponent,
  isTocComponent,
} from '../types';
import { ThemeConfig } from '../styles';
import {
  getPageSetup,
  getPageDimensions,
  pageSizeDiffers,
  themeForSectionPage,
} from '../styles';
// import { pointsToTwips } from '../styles/utils/styleHelpers';
import { ProcessedSection } from './structure';
import {
  getAvailableWidthTwips,
  relativeLengthToTwips,
} from '../utils/widthUtils';

export interface LayoutPlan {
  sections: SectionLayout[];
}

export interface WordSectionProperties {
  page: {
    size: {
      width: number;
      height: number;
      /**
       * OOXML `w:pgSz/@w:code` — the DEVMODE paper code printer drivers key
       * off. Set only for a named size; a custom `{width, height}` has none.
       */
      code?: number;
    };
    margin: {
      top: number;
      right: number;
      bottom: number;
      left: number;
      // Present only when a section overrides them; the theme's page setup
      // states the four edges and leaves these to Word's own defaults.
      header?: number;
      footer?: number;
      gutter?: number;
    };
  };
  column: ColumnSettings & {
    /**
     * The same explicit columns as `children`, in twips.
     *
     * `children` holds docx `Column` instances, which keep their width and
     * space private, so anything downstream of a renderer boundary cannot read
     * them back. This is the plain-numbers copy the DocxIR compiler reads.
     */
    widths?: Array<{ width: number; space?: number }>;
  };
  type?: 'continuous' | 'nextColumn' | 'nextPage' | 'evenPage' | 'oddPage';
}

export interface SectionLayout {
  properties: WordSectionProperties;
  components: ComponentDefinition[];
  layoutType: 'single' | 'multi-column';
  breakBefore: boolean;
  header?: ComponentDefinition[] | 'linkToPrevious';
  footer?: ComponentDefinition[] | 'linkToPrevious';
  /** True if this layout section comes from a user-defined Section component */
  isUserSection: boolean;
  /** True if this layout chunk belongs to a user-defined Section (all chunks of that section) */
  belongsToUserSection: boolean;
  /** Page configuration override for this section */
  pageOverride?: {
    size?: 'A4' | 'A3' | 'LETTER' | 'LEGAL' | { width: number; height: number };
    margins?: {
      top?: number;
      bottom?: number;
      left?: number;
      right?: number;
      header?: number;
      footer?: number;
      gutter?: number;
    };
  };
  /**
   * Set when the section asked to continue on the page but its paper size or
   * orientation differs from the section before it. A continuous break cannot
   * change either, so the layout started it on a new page instead; the
   * compiler reports that the author's request was overridden.
   */
  forcedPageBreak?: boolean;
}

export interface LayoutGroup {
  layout: 'single' | 'multi-column';
  components: ComponentDefinition[];
  breakBefore: boolean;
}

/**
 * Apply layout to processed sections
 */
export function applyLayout(
  sections: ProcessedSection[],
  theme: ThemeConfig,
  themeName: string
): LayoutPlan {
  const layoutSections: SectionLayout[] = [];

  // A continuous break cannot change the paper: Word starts a new page anyway
  // and LibreOffice keeps the old size. So a section that asked to continue
  // but lands on a different size or orientation is told to start a new page,
  // which is what every reader will then agree on. Margins may change mid-page.
  const push = (layoutSection: SectionLayout): void => {
    const previous = layoutSections[layoutSections.length - 1];
    if (
      previous &&
      layoutSection.properties.type === 'continuous' &&
      pageSizeDiffers(
        previous.properties.page.size,
        layoutSection.properties.page.size
      )
    ) {
      layoutSection.properties.type = 'nextPage';
      layoutSection.breakBefore = true;
      layoutSection.forcedPageBreak = true;
    }
    layoutSections.push(layoutSection);
  };

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const layoutGroups = analyzeLayoutGroups(section.components);

    // Determine if this comes from an explicit Section component
    const isSourceUserSection = Boolean(section.isExplicitSection);

    // Handle sections with no components but with headers/footers
    // This ensures headers/footers are rendered even for empty sections
    if (layoutGroups.length === 0 && (section.header || section.footer)) {
      const isFirstSection = i === 0;
      const breakBefore = isFirstSection || Boolean(section.pageBreak);
      const sectionType = breakBefore ? 'nextPage' : 'continuous';

      push({
        properties: createSectionProperties(
          getColumnSettings('single'),
          theme,
          themeName,
          sectionType,
          section.page
        ),
        components: [],
        layoutType: 'single',
        breakBefore,
        header: section.header,
        footer: section.footer,
        isUserSection: isSourceUserSection,
        belongsToUserSection: isSourceUserSection,
        pageOverride: section.page,
      });
      continue;
    }

    for (let j = 0; j < layoutGroups.length; j++) {
      const group = layoutGroups[j];

      // Special handling: a single Columns component becomes its own section with explicit column children
      const isSingleColumnsGroup =
        group.components.length === 1 &&
        isColumnsComponent(group.components[0]);

      const columnSettings = isSingleColumnsGroup
        ? createColumnSettingsFromConfig(
            group.components[0],
            // Percentages are of this section's text measure, not the theme's.
            theme ? themeForSectionPage(theme, section.page) : theme,
            themeName
          )
        : getColumnSettings(group.layout);

      // Only break page if explicitly requested or this is the first section
      const isFirstSection = i === 0 && j === 0;
      // For titleless sections with pageBreak, apply break to first layout group
      const titlelessSectionBreak = Boolean(section.pageBreak && j === 0);
      const breakBefore =
        group.breakBefore || isFirstSection || titlelessSectionBreak;
      const sectionType = breakBefore ? 'nextPage' : 'continuous';

      // Build content components depending on whether this is an explicit columns group
      const contentComponents = isSingleColumnsGroup
        ? processLayoutComponents(
            (
              group.components[0] as unknown as {
                children?: ComponentDefinition[];
              }
            ).children || []
          )
        : processLayoutComponents(group.components);

      push({
        properties: createSectionProperties(
          columnSettings,
          theme,
          themeName,
          sectionType,
          section.page
        ),
        components: contentComponents,
        layoutType: group.layout,
        breakBefore,
        header: section.header,
        footer: section.footer,
        // Only mark the FIRST layout section as user-defined to avoid
        // creating bookmark paragraphs in subsequent sections (e.g., when
        // transitioning to columns layout). This prevents unwanted newlines
        // at the start of column content.
        isUserSection: isSourceUserSection && j === 0,
        belongsToUserSection: isSourceUserSection,
        // Pass page override for this section
        pageOverride: section.page,
      });
    }
  }

  return { sections: layoutSections };
}

/**
 * Analyze components to determine layout groups
 */
export function analyzeLayoutGroups(
  components: ComponentDefinition[]
): LayoutGroup[] {
  const groups: LayoutGroup[] = [];
  let currentLayout: 'single' | 'multi-column' = 'single';
  let currentComponents: ComponentDefinition[] = [];
  let breakNext = false;

  for (const component of components) {
    // Always isolate explicit Columns components into their own group
    if (isColumnsComponent(component)) {
      if (currentComponents.length > 0) {
        groups.push({
          layout: currentLayout,
          components: currentComponents,
          breakBefore: breakNext,
        });
        currentComponents = [];
        breakNext = false;
      }
      // Determine the actual layout based on the columns component configuration
      const columnsLayout = determineComponentLayout(component);
      groups.push({
        layout: columnsLayout,
        components: [component],
        breakBefore: breakNext,
      });
      breakNext = false;
      continue;
    }

    const componentLayout = determineComponentLayout(component);

    // Check for explicit break indicators
    if (hasPageBreak(component)) {
      if (currentComponents.length > 0) {
        // Finish previous group without break
        groups.push({
          layout: currentLayout,
          components: currentComponents,
          breakBefore: breakNext,
        });
        currentComponents = [];
        breakNext = false;
      }
      // Start new group with break for this component
      currentComponents = [component];
      breakNext = true;
      continue;
    }

    // Layout change requires new group
    if (componentLayout !== currentLayout && currentComponents.length > 0) {
      groups.push({
        layout: currentLayout,
        components: currentComponents,
        breakBefore: breakNext,
      });
      currentLayout = componentLayout;
      currentComponents = [component];
      breakNext = false;
    } else {
      currentComponents.push(component);
    }
  }

  // Add remaining components
  if (currentComponents.length > 0) {
    groups.push({
      layout: currentLayout,
      components: currentComponents,
      breakBefore: breakNext,
    });
  }

  return groups;
}

/**
 * Determine layout type for a component
 */
export function determineComponentLayout(
  component: ComponentDefinition
): 'single' | 'multi-column' {
  if (isColumnsComponent(component)) {
    const columns = (component as any).props?.columns;

    // If columns is an array with specific configurations
    if (Array.isArray(columns)) {
      // Any columns array with 2+ items is multi-column
      return columns.length >= 2 ? 'multi-column' : 'single';
    }

    // No valid array provided; treat as single-column
    return 'single';
  }

  // A container is never a column layout, whatever it holds. A `columns`
  // nested in a group, a text box or a block compiles to a table (see
  // `compileColumns`), and declaring the section multi-column around it put
  // that table inside a two-column newspaper section: a KPI row after any
  // paragraph rendered at half the measure with the next paragraph flowing
  // up beside it (#343). Only a top-level `columns` decides a section's
  // column layout, and that case returned above.
  return 'single';
}

/**
 * Process components for layout (insert breaks where needed)
 */
export function processLayoutComponents(
  components: ComponentDefinition[]
): ComponentDefinition[] {
  const processed: ComponentDefinition[] = [];

  for (let i = 0; i < components.length; i++) {
    const component = components[i];

    // Check if this component should have a column break
    if (shouldInsertColumnBreak()) {
      // Create a new component with columnBreak flag
      if (isHeadingComponent(component)) {
        processed.push({
          ...component,
          props: {
            ...((component as any).props || {}),
            columnBreak: true,
          },
        } as ComponentDefinition);
      } else {
        processed.push(component);
      }
    } else {
      processed.push(component);
    }
  }

  return processed;
}

/**
 * Determine if a column break should be inserted
 */
function shouldInsertColumnBreak(): boolean {
  // Logic to determine column breaks based on content distribution
  // This is simplified - could be enhanced with more sophisticated logic
  return false;
}

/**
 * Check if component indicates a page break
 */
function hasPageBreak(component: ComponentDefinition): boolean {
  if (isSectionComponent(component)) {
    // Default to true for section components when pageBreak is undefined
    return component.props?.pageBreak !== false;
  }
  if (isHeadingComponent(component)) {
    return component.props.pageBreak === true;
  }
  if (isParagraphComponent(component)) {
    return component.props.pageBreak === true;
  }
  // Allow TOC components to request a page break as well
  if (isTocComponent(component)) {
    return component.props?.pageBreak === true;
  }
  return false;
}

/**
 * Get column settings for layout type
 */
export function getColumnSettings(
  layout: 'single' | 'multi-column'
): ColumnSettings {
  switch (layout) {
    case 'multi-column':
      // Default multi-column layout: 2 equal-width columns
      // Note: Specific column components will override this with their own settings via createColumnSettingsFromConfig
      return {
        count: 2,
        equalWidth: true,
        space: 720, // 0.5 inch in twips
      };
    case 'single':
    default:
      return { count: 1 };
  }
}

/**
 * Create Word section properties
 */
export function createSectionProperties(
  columnSettings: ColumnSettings,
  theme: ThemeConfig,
  themeName: string,
  sectionType?: 'continuous' | 'nextPage',
  pageOverride?: {
    size?: 'A4' | 'A3' | 'LETTER' | 'LEGAL' | { width: number; height: number };
    margins?: {
      top?: number;
      bottom?: number;
      left?: number;
      right?: number;
      header?: number;
      footer?: number;
      gutter?: number;
    };
  }
): WordSectionProperties {
  // Get base page setup from theme
  const basePageSetup = getPageSetup(theme, themeName);

  // Apply section-level page overrides if present.
  //
  // The size override replaces the base size wholesale rather than merging
  // into it: a custom `{width, height}` is not a standard paper, so it must not
  // inherit the theme's paper code — which a spread over `basePageSetup.size`
  // would silently do, telling the driver to fetch A4 for a custom page.
  const pageSetup = pageOverride
    ? {
        size: pageOverride.size
          ? getPageDimensions(pageOverride.size)
          : basePageSetup.size,
        margin: {
          ...basePageSetup.margin,
          ...(pageOverride.margins || {}),
        },
      }
    : basePageSetup;

  const properties: WordSectionProperties = {
    page: pageSetup,
    column: {
      count: columnSettings.count,
      equalWidth: columnSettings.equalWidth,
      space: columnSettings.space,
    },
  };

  if (
    (columnSettings as any).children &&
    Array.isArray((columnSettings as any).children)
  ) {
    const children = (columnSettings as any).children as {
      width?: number;
      space?: number;
    }[];
    // When we have explicit column children, ensure equalWidth is explicitly false
    // so Word doesn't override widths with equal distribution.
    properties.column.equalWidth = false;
    // Also remove top-level space to avoid conflicting with per-column spaces
    delete properties.column.space;

    properties.column.widths = children.map((c) => ({
      width: c.width as number,
      ...(c.space !== undefined ? { space: c.space } : {}),
    }));
  }

  if (sectionType) {
    properties.type = sectionType === 'continuous' ? 'continuous' : 'nextPage';
  }

  return properties;
}

/**
 * Create explicit column settings from a Columns component props
 * - width/gap accept number (points) or percentage string relative to available width
 */
function createColumnSettingsFromConfig(
  component: ComponentDefinition,
  theme: ThemeConfig,
  themeName: string
): ColumnSettings {
  const cfg = ((component as any).props || {}) as {
    columns?: Array<{ width?: number | string; gap?: number | string }>;
  };

  // Fallback to single column if no valid array is provided
  const columns = (cfg.columns || []) as Array<{
    width?: number | string;
    gap?: number | string;
  }>;

  if (!Array.isArray(columns) || columns.length === 0) {
    return { count: 1 };
  }

  const availableWidthTwips = getAvailableWidthTwips(theme, themeName);

  // First pass: compute defined widths/spaces; collect indexes without width
  const childSettings: { width?: number; space?: number }[] = [];
  let totalDefinedWidth = 0;
  let totalSpace = 0;
  const unspecifiedWidthIndexes: number[] = [];

  for (let i = 0; i < columns.length; i++) {
    const c = columns[i];
    const child: { width?: number; space?: number } = {};

    if (c.width !== undefined) {
      const widthTwips = relativeLengthToTwips(c.width, availableWidthTwips);
      if (widthTwips > 0) {
        child.width = widthTwips;
        totalDefinedWidth += widthTwips;
      }
    } else {
      unspecifiedWidthIndexes.push(i);
    }

    if (c.gap !== undefined) {
      const spaceTwips = relativeLengthToTwips(c.gap, availableWidthTwips);
      if (spaceTwips > 0) {
        child.space = spaceTwips;
        totalSpace += spaceTwips;
      }
    }

    childSettings.push(child);
  }

  // Validate that defined widths + gaps do not exceed available width (with tolerance)
  const totalUsedTwips = totalDefinedWidth + totalSpace;
  const tolerance = 0.005; // 0.5%
  const allowed = availableWidthTwips * (1 + tolerance);
  if (totalUsedTwips > allowed) {
    const usedPercent = (totalUsedTwips / availableWidthTwips) * 100;
    // Build diagnostics per column
    const details = childSettings
      .map((c, idx) => {
        const wPct = c.width
          ? ((c.width / availableWidthTwips) * 100).toFixed(1) + '%'
          : 'auto/unspecified';
        const gPct = c.space
          ? ((c.space / availableWidthTwips) * 100).toFixed(1) + '%'
          : '0%';
        return `  - Column ${idx + 1}: width=${wPct}, gap=${gPct}`;
      })
      .join('\n');
    const suggestion =
      'Reduce one or more widths/gaps so total <= 100%, remove the last gap, or set some widths to "auto" to consume remaining space.';
    throw new Error(
      `Columns configuration exceeds available width: widths + gaps = ${usedPercent.toFixed(1)}% (> 100% + ${(
        tolerance * 100
      ).toFixed(
        1
      )}% tolerance)\nDetails:\n${details}\nSuggestion: ${suggestion}`
    );
  }

  // Distribute remaining width equally among unspecified widths
  const remaining = Math.max(
    0,
    availableWidthTwips - totalSpace - totalDefinedWidth
  );
  if (unspecifiedWidthIndexes.length > 0) {
    const each = Math.floor(remaining / unspecifiedWidthIndexes.length);
    for (const idx of unspecifiedWidthIndexes) {
      childSettings[idx].width = each;
    }
  }

  return {
    count: columns.length,
    equalWidth: false,
    children: childSettings,
  };
}

/**
 * Calculate optimal column distribution for content
 */
export function calculateColumnDistribution(
  components: ComponentDefinition[],
  columnCount: number
): ComponentDefinition[][] {
  if (columnCount === 1) {
    return [components];
  }

  // Simple distribution - could be enhanced with content-aware logic
  const columns: ComponentDefinition[][] = Array(columnCount)
    .fill(null)
    .map(() => []);
  let currentColumn = 0;

  for (const component of components) {
    columns[currentColumn].push(component);
    currentColumn = (currentColumn + 1) % columnCount;
  }

  return columns;
}
